import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRightCircle, CalendarCheck, MessageCircle, Pencil, Phone, Send, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CALL_OUTCOMES, QUOTE_CATEGORIES, QUOTE_STATUS, QUOTE_TONE, fmtDateTime, fmtDay, fmtEuro } from "@/lib/format";
import { Btn, Pill, Spinner } from "@/components/rp";
import { AccessData, DocItem, EmailLog, Info, InternalNote, Section } from "@/components/commesse";
import { CallDialog, QuoteFormDialog, StatusDialog, SurveyDialog, surveyText } from "@/components/admin/quoteDialogs";
import { EdiliziaPanel } from "@/components/admin/edilizia";
import { SopralluoghiPanel } from "@/components/admin/sopralluoghi";

const SurveyBox = ({ q, run, open }) => {
  const s = q.survey;
  const active = s && ["programmato", "proposto"].includes(s.status);
  return (
    <Section label="Sopralluogo preliminare" testid="quote-survey-section">
      {!s || !active ? (
        <p className="text-sm text-[#757575] mb-4">{s ? `Ultimo sopralluogo: ${s.status}${s.outcome_note ? ` — ${s.outcome_note}` : ""}. ` : "Nessun sopralluogo. "}Puoi fissarne uno prima che il cliente accetti il preventivo: lo vedrà nell'Area Clienti e potrà confermarlo.</p>
      ) : s.status === "proposto" ? (
        <div className="text-sm mb-4 space-y-1"><p>In attesa che il cliente scelga uno degli orari proposti:</p>{s.proposals.map((p, i) => <p key={i} className="text-[#757575]">• {fmtDay(p.date)} ore {p.start_time}{p.end_time && `–${p.end_time}`}</p>)}</div>
      ) : (
        <div className="text-sm mb-4"><p className="font-medium">{surveyText(s)}</p><p className="text-[#757575]">{s.client_confirmed_at ? `Confermato dal cliente il ${fmtDateTime(s.client_confirmed_at)}` : "Non ancora confermato dal cliente"}</p></div>
      )}
      {["bozza", "inviato", "in_trattativa"].includes(q.status) && (
        <div className="flex flex-wrap gap-2">
          <Btn className="h-10" onClick={() => open("fix")} data-testid="survey-fix-button"><CalendarCheck className="h-4 w-4" /> {active ? "Riprogramma" : "Fissa sopralluogo"}</Btn>
          <Btn variant="outline" className="h-10" onClick={() => open("propose")} data-testid="survey-propose-button">Proponi 2–3 orari</Btn>
          {s?.status === "programmato" && <Btn variant="secondary" className="h-10" onClick={() => run(`/quotes/${q.id}/survey/outcome`, { status: "completato" })} data-testid="survey-complete-button">Segna effettuato</Btn>}
          {active && <Btn variant="danger" className="h-10" onClick={() => run(`/quotes/${q.id}/survey/outcome`, { status: "annullato" })} data-testid="survey-cancel-button">Annulla</Btn>}
        </div>
      )}
    </Section>
  );
};

const DataBox = ({ q, run }) => {
  const reports = (q.logistics_reports || []).filter((r) => !r.resolved_at);
  return (
    <Section label="Dati preventivo" testid="quote-data-section">
      <div className="grid sm:grid-cols-2 gap-4">
        <Info label="Importo">{fmtEuro(q.amount)}</Info>
        <Info label="Valido fino al">{fmtDay(q.valid_until)}</Info>
        <Info label="Richiamo">{q.recall_date ? <>{fmtDay(q.recall_date)}{q.recall_note && <span className="block text-xs text-[#757575]">{q.recall_note}</span>}</> : "Nessun richiamo"}</Info>
        <Info label="Indirizzo del cantiere">{q.delivery_address}</Info>
      </div>
      <div className="mt-4"><AccessData x={q} /></div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Info label="Verifica del cliente">{q.logistics_confirmed_at ? `Dati confermati il ${fmtDateTime(q.logistics_confirmed_at)}` : "Non ancora confermati dal cliente"}</Info>
        <Info label="Aperto dal cliente">{q.viewed_at ? fmtDateTime(q.viewed_at) : "Non ancora"}</Info>
      </div>
      {reports.length > 0 && (
        <div className="mt-4 rounded-2xl border border-[#FFC107] bg-[#FFC107]/10 p-4" data-testid="quote-logistics-reports">
          <p className="text-sm font-medium">Correzioni segnalate dal cliente</p>
          {reports.map((r) => <p key={r.id} className="text-sm mt-1">«{r.text}» · {fmtDateTime(r.at)}</p>)}
          <Btn className="h-9 mt-3" onClick={() => run(`/quotes/${q.id}/logistics-reports/resolve`)} data-testid="quote-logistics-resolve">Dati corretti: chiudi segnalazioni</Btn>
        </div>
      )}
      <div className="mt-5"><p className="rp-label mb-1">Descrizione per il cliente</p><p className="text-sm whitespace-pre-wrap">{q.client_description || "—"}</p></div>
      <div className="mt-5"><InternalNote text={q.internal_notes} /></div>
    </Section>
  );
};

const DocsBox = ({ q, setQ }) => {
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { setQ((await api.post(`/quotes/${q.id}/documents`, fd)).data); toast.success("Documento caricato"); } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const del = async (d) => {
    try { setQ((await api.delete(`/quotes/${q.id}/documents/${d.id}`)).data); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Section label="Documenti del preventivo" testid="quote-docs-section">
      <label className="rp-btn border border-[#EBEBEB] bg-white hover:bg-[#F7F7F7] cursor-pointer mb-4" data-testid="quote-doc-upload">
        <Upload className="h-4 w-4" /> Carica PDF o immagine
        <input type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="quote-doc-file-input" />
      </label>
      <div className="space-y-2">
        {!q.documents.length && <p className="text-sm text-[#757575]">Nessun documento.</p>}
        {q.documents.map((d) => (
          <DocItem key={d.id} d={d} href={`/api/quotes/${q.id}/documents/${d.id}/file`}>
            {!q.order_id && <button onClick={() => del(d)} className="text-[#757575] hover:text-[#DC3545]" data-testid={`quote-doc-delete-${d.id}`}><X className="h-4 w-4" /></button>}
          </DocItem>
        ))}
      </div>
    </Section>
  );
};

const HistoryBox = ({ q }) => (
  <Section label="Storico stato" testid="quote-history-section">
    <div className="space-y-3">
      {[...(q.history || [])].reverse().map((h, i) => (
        <div key={i} className="text-sm border-l-2 border-[#C8BDAB] pl-3">
          <p className="font-medium">{QUOTE_STATUS[h.status]} · <span className="text-[#757575] font-normal">{fmtDateTime(h.at)} · {h.by}</span></p>
          <p className="text-[#757575]">{h.note}</p>
        </div>
      ))}
    </div>
    {q.calls?.length > 0 && <p className="text-xs text-[#757575] mt-4">Ultima telefonata: {CALL_OUTCOMES[q.last_call_outcome]} · {fmtDateTime(q.last_call_at)}</p>}
  </Section>
);

export default function AdminPreventivoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => navigate("/admin/preventivi")); }, [id, navigate]);

  const run = async (url, body = {}) => {
    try { setQ((await api.post(url, body)).data); toast.success("Fatto"); } catch (e) { toast.error(errMsg(e)); }
  };
  const whatsapp = async () => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/quotes/${id}/whatsapp`);
      const url = `https://wa.me/${data.phone}?text=${encodeURIComponent(data.text)}`;
      if (w) w.location.href = url; else window.location.href = url;
      if (!data.phone) toast.info("Telefono del cliente mancante: scegli il contatto direttamente in WhatsApp");
    } catch (e) { w?.close(); toast.error(errMsg(e)); }
  };
  const convert = async () => {
    if (!window.confirm("Trasformare il preventivo in cantiere? Dati e documenti passano nel nuovo cantiere.")) return;
    try {
      const { data } = await api.post(`/quotes/${id}/convert`);
      if (data.client_id && !data.client_password) toast.success("Cantiere creato. Se il cliente è nuovo, gli abbiamo inviato l'email per scegliere la sua password.", { duration: 10000 });
      navigate(`/admin/commesse/${data.order_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente il preventivo?")) return;
    try { await api.delete(`/quotes/${id}`); navigate("/admin/preventivi"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!q) return <Spinner />;
  const open = ["bozza", "inviato", "in_trattativa"].includes(q.status);
  return (
    <div className="space-y-6">
      <Link to="/admin/preventivi" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="back-to-quotes"><ArrowLeft className="h-4 w-4" /> Preventivi</Link>
      <div className="flex flex-wrap items-start justify-between gap-6 rp-fade">
        <div>
          <p className="text-xs font-mono text-[#757575]">{q.code} · {QUOTE_CATEGORIES[q.category] || q.category}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="quote-title">{q.title}</h1>
          <p className="text-sm text-[#757575] mt-1">Cliente: {q.client_id ? <Link to={`/admin/clienti/${q.client_id}`} className="text-black underline">{q.client_name}</Link> : `${q.client_name} (di passaggio)`} · Referente: {q.seller_name}</p>
          <div className="mt-3"><Pill tone={QUOTE_TONE[q.status]} data-testid="quote-status">{QUOTE_STATUS[q.status]}</Pill></div>
        </div>
        <div className="flex flex-wrap gap-2 max-w-xl justify-end">
          {!q.order_id && <Btn variant="outline" onClick={() => setDialog("edit")} data-testid="quote-edit-button"><Pencil className="h-4 w-4" /> Modifica</Btn>}
          {open && <Btn variant="outline" onClick={() => run(`/quotes/${id}/send`)} data-testid="quote-send-button"><Send className="h-4 w-4" /> {q.status === "bozza" ? "Invia al cliente" : "Reinvia al cliente"}</Btn>}
          {["inviato", "in_trattativa"].includes(q.status) && !q.order_id && <Btn variant="outline" onClick={whatsapp} className="text-[#128C7E] border-[#128C7E]/40" data-testid="quote-whatsapp-button"><MessageCircle className="h-4 w-4" /> Invia su WhatsApp</Btn>}
          {open && <Btn variant="outline" onClick={() => setDialog("call")} data-testid="quote-call-button"><Phone className="h-4 w-4" /> Esito telefonata</Btn>}
          {!q.order_id && <Btn variant="outline" onClick={() => setDialog("status")} data-testid="quote-status-button">Aggiorna stato / richiamo</Btn>}
          {q.order_id ? <Btn onClick={() => navigate(`/admin/commesse/${q.order_id}`)} data-testid="quote-open-order"><ArrowRightCircle className="h-4 w-4" /> Apri il cantiere</Btn>
            : !["rifiutato", "scaduto"].includes(q.status) && <Btn onClick={convert} data-testid="quote-convert-button"><ArrowRightCircle className="h-4 w-4" /> Trasforma in cantiere</Btn>}
          {!q.order_id && <Btn variant="danger" onClick={remove} data-testid="quote-delete-button"><Trash2 className="h-4 w-4" /> Elimina</Btn>}
        </div>
      </div>
      {q.accepted_at && <p className="text-sm text-[#198754]" data-testid="quote-accepted-info">Preventivo accettato e firmato da {q.accepted_by} il {fmtDateTime(q.accepted_at)} (dall'Area Clienti)</p>}
      {q.rejection_note && <p className="text-sm text-[#DC3545]">Rifiutato dal cliente: «{q.rejection_note}»</p>}
      <SurveyBox q={q} run={run} open={(m) => setDialog(`survey-${m}`)} />
      <div className="rp-card p-5 sm:p-6" data-testid="quote-sopralluoghi"><SopralluoghiPanel compact showQuote={false} filter={{ quote_id: q.id }} preset={{ client_id: q.client_id || null, client_name: q.client_name, client_phone: q.client_phone || "", client_email: q.client_email || "", address: q.delivery_address || "", piano: q.delivery_floor || "", ascensore: q.elevator ?? null, ztl: q.ztl ?? null, link_quote_id: q.id }} /></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <DataBox q={q} run={run} />
        <div className="space-y-6"><DocsBox q={q} setQ={setQ} /><HistoryBox q={q} /><EmailLog refId={q.id} reload={q.history?.length} /></div>
      </div>
      <EdiliziaPanel base={`/quotes/${q.id}`} quote={q} onQuote={setQ} note={q.order_id ? "Il preventivo è già un cantiere: i dati edilizi aggiornati si gestiscono nel cantiere." : "Immobile, pratiche e computo passano in automatico al cantiere quando il preventivo viene accettato."} />
      <QuoteFormDialog open={dialog === "edit"} onClose={() => setDialog(null)} quote={q} onSaved={setQ} />
      {dialog === "call" && <CallDialog quote={q} onClose={() => setDialog(null)} onSaved={setQ} />}
      {dialog === "status" && <StatusDialog quote={q} onClose={() => setDialog(null)} onSaved={setQ} />}
      {dialog?.startsWith("survey-") && <SurveyDialog quote={q} mode={dialog.slice(7)} onClose={() => setDialog(null)} onSaved={setQ} />}
    </div>
  );
}
